import { Link } from "@tanstack/react-router";

import Container from "@/components/Container";
import Eyebrow from "@/components/Eyebrow";
import Reveal from "@/components/Reveal";
import Section from "@/components/Section";
import { CtaButton } from "@/components/CtaButton";

const BULLETS = [
  "Ranked opportunities across 6 areas of your operations",
  "Impact and effort estimate for each",
  "A 30-day starting plan",
] as const;

const OPTIONS = [
  { label: "Less than 10%", selected: false },
  { label: "10–25%", selected: false },
  { label: "25–50%", selected: true },
  { label: "More than half", selected: false },
] as const;

/** Decorative, non-interactive mock of the AI Opportunity Scorecard tool. */
function ToolPreview() {
  return (
    <div
      aria-hidden="true"
      className="select-none rounded-xl border border-ink-200 bg-paper-0 p-6 shadow-[0_24px_60px_-20px_oklch(0.1831_0.0309_263.38_/_0.22)]"
    >
      <div className="flex items-center justify-between font-mono text-eyebrow uppercase font-medium text-ink-400">
        <span>Question 08 / 12</span>
        <span>~90 sec left</span>
      </div>

      <div className="mt-3 h-1 w-full rounded-full bg-ink-100">
        <div className="h-full rounded-full bg-brand-600" style={{ width: "66%" }} />
      </div>

      <p className="mt-6 font-display text-h3 text-ink-900">
        How much of your team’s week goes to repetitive manual work?
      </p>

      <div className="mt-5 space-y-3">
        {OPTIONS.map((option) => (
          <div
            key={option.label}
            className={
              option.selected
                ? "rounded-full border border-brand-600 bg-brand-100 px-5 py-3 text-body font-medium text-ink-900"
                : "rounded-full border border-ink-100 px-5 py-3 text-body text-ink-600"
            }
          >
            {option.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScoreCta() {
  return (
    <Section tone="raised">
      <Container>
        <Reveal>
          <div className="relative overflow-hidden rounded-xl border border-ink-200 bg-paper-0 p-8 sm:p-12 lg:p-16">
            <div className="relative grid grid-cols-1 items-center gap-12 lg:grid-cols-[52fr_48fr] lg:gap-16">
              <div>
                <Eyebrow variant="teal">Free tool</Eyebrow>
                <h2
                  className="mt-3 font-display text-h2 text-ink-900"
                  style={{ textWrap: "balance" }}
                >
                  See where AI could pay in your business. In four minutes.
                </h2>
                <p className="mt-4 measure text-body text-ink-600">
                  Answer 12 questions about how your business runs. Get a ranked map of where AI
                  could unlock value for you specifically, with an estimate of impact and effort for
                  each — emailed to you as a PDF.
                </p>

                <ul className="mt-6 space-y-2.5">
                  {BULLETS.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-2.5 text-body text-ink-600">
                      <span
                        aria-hidden="true"
                        className="mt-2.5 size-1.5 shrink-0 rounded-full bg-brand-600"
                      />
                      {bullet}
                    </li>
                  ))}
                </ul>

                <CtaButton asChild className="mt-8 w-full whitespace-normal text-center sm:w-auto sm:whitespace-nowrap">
                  <Link to="/tools/ai-opportunity-scorecard">Get My AI Opportunity Map</Link>
                </CtaButton>
              </div>

              <ToolPreview />
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}

export default ScoreCta;
